import { apiAvailableTimeToAvailabilityRule } from '@app/utils/available_times'
import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiAvailableTime } from '@app/packages/api/v2/types/entities'
import type { AvailableTimesPutRequest } from '@app/packages/api/v2/types/params'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { routingUUIDSelector } from './routing'

const availableTimesSlice = createReduxSlice<Record<string, ApiAvailableTime[] | undefined>>('available_times')

type AvailableTimesOwner = { type: 'id'; value: string } | { type: 'token'; value: string }

export const fetchAvailableTimes = (owner: AvailableTimesOwner) =>
  createThunk(async dispatch => {
    const [{ getAvailableTimes }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/available_times'), import('@app/store/actions/api')])
    const resp = await dispatch(callV2Api(getAvailableTimes(owner))).catch(catchFetchDataError(dispatch, 'Schedule fetch failed'))
    dispatch(availableTimesSlice.update(state => ({ ...state, [`${owner.type}:${owner.value}`]: resp.data })))
    return resp.data
  })

export const saveAvailableTimes = (request: AvailableTimesPutRequest) =>
  createThunk(async dispatch => {
    const [{ putAvailableTimes }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/available_times'), import('@app/store/actions/api')])
    const resp = await dispatch(callV2Api(putAvailableTimes(request)))
    dispatch(
      availableTimesSlice.update(state => {
        if (!state) return state
        // the current user's own schedule is the only `id:` entry that is ever fetched
        const ownKey = Object.keys(state).find(key => key.startsWith('id:'))
        if (!ownKey) return state
        return { ...state, [ownKey]: resp.data }
      })
    )
  })

export const availableTimesSelector = createSelector([availableTimesSlice.selector], record => record ?? {})

export const availableTimesResultSelector = createSelector([availableTimesSlice.selector, profileUserResultSelector], (record, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (user?.account_type !== 'sitter') return []
    const times = record?.[`id:${user.id}`]
    if (!times)
      return c.load(
        createPromise('Available times must be fetched', user.id, async dispatch => {
          await dispatch(fetchAvailableTimes({ type: 'id', value: user.id }))
        })
      )
    return times
  })
)

export const availabilityRulesSelector = mapThunkSelector(availableTimesResultSelector, times => {
  return times.map(apiAvailableTimeToAvailabilityRule)
})

export const createAvailableTimesByTokenResultSelector = (tokenSelector: (state: unknown) => string | null) =>
  createSelector([tokenSelector, routingUUIDSelector, availableTimesSlice.selector], (token, uuid, record) =>
    createLoadable(c => {
      if (!token) return null
      const schedule = record?.[`token:${token}`]
      if (!schedule)
        return c.load(
          createPromise(`Available times for ${token} must be fetched`, [uuid, token].join(':'), async dispatch => {
            await dispatch(fetchAvailableTimes({ type: 'token', value: token }))
          })
        )
      return schedule
    })
  )
